import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { WorkspaceRoleValue, type WorkspaceRole } from '@/api/types'

export type WorkspaceAction =
  | 'create_project'
  | 'manage_members'
  | 'manage_workspace'
  | 'change_plan'

function minRole(action: WorkspaceAction): WorkspaceRole {
  switch (action) {
    case 'change_plan':
    case 'manage_workspace':
      return WorkspaceRoleValue.OWNER
    case 'manage_members':
      return WorkspaceRoleValue.ADMIN
    case 'create_project':
      return WorkspaceRoleValue.EMPLOYEE
    default:
      return WorkspaceRoleValue.OWNER
  }
}

/**
 * Returns true if the current user has permission for the workspace-level action.
 * OWNER=1, ADMIN=2, EMPLOYEE=3 — lower number = more permission.
 *
 * Fallback: if workspaceStore.myRole is not yet populated (race on cold editor
 * load), derives from authStore.memberships + currentWorkspace.
 */
export function useCan(action: WorkspaceAction): boolean {
  const storedRole = useWorkspaceStore((s) => s.myRole)
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id ?? null)
  const memberships = useAuthStore((s) => s.memberships)

  let role: WorkspaceRole | null = storedRole
  if (role === null && currentWorkspaceId !== null) {
    role = memberships.find((m) => m.workspace_id === currentWorkspaceId)?.role ?? null
  }
  if (role === null && memberships.length === 1) {
    // Only one known workspace — assume it's the active one.
    role = memberships[0].role
  }
  if (role === null) return false
  return role <= minRole(action)
}

/** True if the current user is a workspace owner or admin. */
export function useIsWsAdminOrOwner(): boolean {
  const storedRole = useWorkspaceStore((s) => s.myRole)
  const currentWorkspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id ?? null)
  const memberships = useAuthStore((s) => s.memberships)

  let role: WorkspaceRole | null = storedRole
  if (role === null && currentWorkspaceId !== null) {
    role = memberships.find((m) => m.workspace_id === currentWorkspaceId)?.role ?? null
  }
  if (role === null && memberships.length === 1) {
    role = memberships[0].role
  }
  if (role === null) return false
  return role <= WorkspaceRoleValue.ADMIN
}
